import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { driver, DriveStep } from 'driver.js';

const TOUR_STORAGE_KEY = 'filter-ref-tour-seen';
const GENERAL_SETTING_TOUR_KEY = 'general-setting-tour-seen';

@Injectable({
  providedIn: 'root'
})
export class TourService {

  constructor(private translate: TranslateService) {}

  /**
   * Rende "sicuri" gli step con selettore stringa: l'elemento viene risolto al momento dello step e
   * usato solo se esiste ed è visibile. Se manca o è nascosto (es. tab con display:none, pannello
   * non ancora aperto) la funzione non restituisce nulla e driver.js mostra il popover al centro
   * dello schermo, invece di agganciarlo a un elemento di dimensione zero in alto a sinistra.
   */
  private withVisibleAnchors(steps: DriveStep[]): DriveStep[] {
    return steps.map(step =>
      typeof step.element === 'string'
        ? { ...step, element: this.visibleElement(step.element) }
        : step
    );
  }

  private visibleElement(selector: string): () => Element {
    return () => {
      const el = document.querySelector(selector) as HTMLElement | null;
      if (!el) return undefined as unknown as Element;
      const rect = el.getBoundingClientRect();
      const hasSize = rect.width > 0 && rect.height > 0;
      const inViewport = rect.bottom > 0 && rect.right > 0
        && rect.top < window.innerHeight && rect.left < window.innerWidth;
      const style = window.getComputedStyle(el);
      const rendered = style.display !== 'none' && style.visibility !== 'hidden';
      return hasSize && inViewport && rendered ? el : (undefined as unknown as Element);
    };
  }

  private t(key: string): string {
    return this.translate.instant('global.tour.' + key);
  }

  private buildFilterRefSteps(): DriveStep[] {
    const hasIncludeChildrenToggle = !!document.querySelector('.filter-icons-bar .fa-code-branch');

    const steps: DriveStep[] = [
      // Step 1: Intro
      {
        popover: {
          title: this.t('introTitle'),
          description: this.t('introDesc'),
        }
      },
      // Step 2: Tree view con espansione primo nodo
      {
        element: '.tree-container',
        popover: {
          title: this.t('treeViewTitle'),
          description: this.t('treeViewDesc'),
          side: 'left',
          align: 'start',
          onNextClick: () => {
            const listBtn = document.querySelector('.view-mode-buttons [icon="fa fa-list"]') as HTMLElement;
            if (listBtn) listBtn.click();
            setTimeout(() => this.driverObj?.moveNext(), 300);
          }
        },
        onHighlightStarted: () => {
          const firstNode = document.querySelector('.tree-container .tree-node.model-node, .tree-container .tree-node.customer-node') as HTMLElement;
          if (firstNode) {
            setTimeout(() => firstNode.click(), 400);
          }
        }
      },
      // Step 3: Vista lista
      {
        element: '.list-container',
        popover: {
          title: this.t('listViewTitle'),
          description: this.t('listViewDesc'),
          side: 'left',
          align: 'start',
          onNextClick: () => {
            const buildingBtn = document.querySelector('.view-mode-buttons [icon="fa fa-building"]') as HTMLElement;
            if (buildingBtn) buildingBtn.click();
            setTimeout(() => this.driverObj?.moveNext(), 300);
          }
        },
        onHighlightStarted: () => {
          const listBtn = document.querySelector('.view-mode-buttons [icon="fa fa-list"]') as HTMLElement;
          if (listBtn && !listBtn.classList.contains('active')) {
            listBtn.click();
          }
        }
      },
      // Step 4: Vista customer-tree
      {
        element: '.tree-container',
        popover: {
          title: this.t('customerTreeTitle'),
          description: this.t('customerTreeDesc'),
          side: 'left',
          align: 'start'
        },
        onHighlightStarted: () => {
          setTimeout(() => {
            const firstCustomerNode = document.querySelector('.tree-container .tree-node.customer-node') as HTMLElement;
            if (firstCustomerNode) firstCustomerNode.click();
          }, 400);
        }
      },
      // Step 5: Select all / controlli
      {
        element: '.select-all-container',
        popover: {
          title: this.t('selectAllTitle'),
          description: this.t('selectAllDesc'),
          side: 'top',
          align: 'center'
        },
        onHighlightStarted: () => {
          const treeBtn = document.querySelector('.view-mode-buttons [icon="fa fa-code-branch"]') as HTMLElement;
          if (treeBtn && !treeBtn.classList.contains('active')) {
            treeBtn.click();
          }
        }
      },
      // Step 6: Contatore selezionati / totale
      {
        element: '.filter-count',
        popover: {
          title: this.t('filterCountTitle'),
          description: this.t('filterCountDesc'),
          side: 'bottom',
          align: 'end'
        }
      },
      // Step 7: Toolbar filtri icone
      {
        element: '.filter-icons-bar',
        popover: {
          title: this.t('filterBarTitle'),
          description: this.t('filterBarDesc'),
          side: 'bottom',
          align: 'center'
        }
      }
    ];

    // Step condizionali: solo se showIncludeChildrenToggle è attivo
    if (hasIncludeChildrenToggle) {
      steps.push(
        {
          element: '.filter-icons-bar .fa-code-branch',
          popover: {
            title: this.t('includeChildTitle'),
            description: this.t('includeChildDesc'),
            side: 'bottom',
            align: 'center'
          }
        },
        {
          element: '.filter-icons-bar .fa-ban',
          popover: {
            title: this.t('includeDisabledTitle'),
            description: this.t('includeDisabledDesc'),
            side: 'bottom',
            align: 'center'
          }
        }
      );
    }

    // Have Communicated (sempre visibile)
    steps.push({
      element: '.filter-icons-bar .fa-comments',
      popover: {
        title: this.t('haveCommunicatedTitle'),
        description: this.t('haveCommunicatedDesc'),
        side: 'bottom',
        align: 'center'
      }
    });

    // Applica filtri
    steps.push({
      element: '#applyBtn',
      popover: {
        title: this.t('applyTitle'),
        description: this.t('applyDesc'),
        side: 'bottom',
        align: 'start'
      }
    });

    // Refresh dati
    steps.push({
      element: '.fa-arrows-rotate',
      popover: {
        title: this.t('refreshTitle'),
        description: this.t('refreshDesc'),
        side: 'bottom',
        align: 'end'
      }
    });

    // Nota finale - limite 100 asset
    steps.push({
      popover: {
        title: this.t('limitTitle'),
        description: this.t('limitDesc'),
      }
    });

    return steps;
  }

  private tGs(key: string): string {
    return this.translate.instant('global.tourGeneralSetting.' + key);
  }

  private buildGeneralSettingSteps(isCustomerAudience: boolean): DriveStep[] {
    const steps: DriveStep[] = [
      // Step 1: Intro
      {
        popover: {
          title: this.tGs('introTitle'),
          description: this.tGs('introDesc'),
        }
      },
      // Step 2: Sezione Admin (primo mat-tab-group)
      {
        element: '#gs-tour-admin-tabs',
        popover: {
          title: this.tGs('adminTabsTitle'),
          description: this.tGs('adminTabsDesc'),
          side: 'bottom',
          align: 'start'
        }
      },
      // Step 3: Tab Catalogo
      {
        element: '#gs-tour-tab-catalog',
        popover: {
          title: this.tGs('catalogTabTitle'),
          description: this.tGs('catalogTabDesc'),
          side: 'bottom',
          align: 'start'
        }
      },
    ];

    // Step 4: Tab KPI (solo audience CUSTOMER)
    if (isCustomerAudience) {
      steps.push({
        element: '#gs-tour-tab-kpi',
        popover: {
          title: this.tGs('kpiTabTitle'),
          description: this.tGs('kpiTabDesc'),
          side: 'bottom',
          align: 'start'
        }
      });
    }

    // Step 5: Toggle sezione configurazione
    steps.push({
      element: '.config-section-toggle-btn',
      popover: {
        title: this.tGs('configToggleTitle'),
        description: this.tGs('configToggleDesc'),
        side: 'bottom',
        align: 'start',
        onNextClick: () => {
          const adminTabs = document.getElementById('gs-tour-admin-tabs');
          const isAdminVisible = adminTabs && !adminTabs.closest('.section-hidden');
          if (isAdminVisible) {
            (document.querySelector('.config-section-toggle-btn') as HTMLElement)?.click();
          }
          setTimeout(() => this.driverObj?.moveNext(), 400);
        }
      }
    });

    // Step 6-9: Tab configurazione (visibili dopo il toggle)
    steps.push(
      {
        element: '#gs-tour-tab-map',
        popover: {
          title: this.tGs('mapTabTitle'),
          description: this.tGs('mapTabDesc'),
          side: 'bottom',
          align: 'start'
        }
      },
      {
        element: '#gs-tour-tab-journey',
        popover: {
          title: this.tGs('journeyTabTitle'),
          description: this.tGs('journeyTabDesc'),
          side: 'bottom',
          align: 'start'
        }
      },
      {
        element: '#gs-tour-tab-dashboard',
        popover: {
          title: this.tGs('dashboardTabTitle'),
          description: this.tGs('dashboardTabDesc'),
          side: 'bottom',
          align: 'start'
        }
      },
      {
        element: '#gs-tour-tab-graphics',
        popover: {
          title: this.tGs('graphicsTabTitle'),
          description: this.tGs('graphicsTabDesc'),
          side: 'bottom',
          align: 'start'
        }
      },
      // Step 10: Salva e ereditarietà (selettore limitato alla sezione visibile: la tab admin nascosta ha lo stesso header)
      {
        element: '.settings-tabs:not(.section-hidden) .configuration-title .save-btn',
        popover: {
          title: this.tGs('saveBtnTitle'),
          description: this.tGs('saveBtnDesc'),
          side: 'bottom',
          align: 'start'
        }
      },
      // Step 11: Locale vs Ereditata
      {
        element: '.settings-tabs:not(.section-hidden) .configuration-title .title-text',
        popover: {
          title: this.tGs('inheritanceTitle'),
          description: this.tGs('inheritanceDesc'),
          side: 'bottom',
          align: 'start'
        }
      },
      // Step 12: Outro
      {
        popover: {
          title: this.tGs('outroTitle'),
          description: this.tGs('outroDesc'),
        }
      }
    );

    return steps;
  }

  startGeneralSettingTour(isCustomerAudience: boolean = true): void {
    this.driverObj = driver({
      showProgress: true,
      animate: true,
      allowClose: true,
      overlayColor: 'rgba(0, 0, 0, 0.5)',
      stagePadding: 8,
      stageRadius: 0,
      popoverClass: 'no-radius-popover',
      nextBtnText: this.tGs('nextBtn'),
      prevBtnText: this.tGs('prevBtn'),
      doneBtnText: this.tGs('doneBtn'),
      steps: this.withVisibleAnchors(this.buildGeneralSettingSteps(isCustomerAudience)),
      onDestroyStarted: () => {
        localStorage.setItem(GENERAL_SETTING_TOUR_KEY, 'true');
        this.driverObj.destroy();
      }
    });

    this.driverObj.drive();
  }

  hasSeenGeneralSettingTour(): boolean {
    return localStorage.getItem(GENERAL_SETTING_TOUR_KEY) === 'true';
  }

  resetGeneralSettingTour(): void {
    localStorage.removeItem(GENERAL_SETTING_TOUR_KEY);
  }

  private driverObj: any;

  startFilterRefTour(): void {
    this.driverObj = driver({
      showProgress: true,
      animate: true,
      allowClose: true,
      overlayColor: 'rgba(0, 0, 0, 0.5)',
      stagePadding: 8,
      stageRadius: 0,
      popoverClass: 'no-radius-popover',
      nextBtnText: this.t('nextBtn'),
      prevBtnText: this.t('prevBtn'),
      doneBtnText: this.t('doneBtn'),
      steps: this.withVisibleAnchors(this.buildFilterRefSteps()),
      onDestroyStarted: () => {
        localStorage.setItem(TOUR_STORAGE_KEY, 'true');
        this.driverObj.destroy();
      }
    });

    this.driverObj.drive();
  }

  hasSeenTour(): boolean {
    return localStorage.getItem(TOUR_STORAGE_KEY) === 'true';
  }

  resetTour(): void {
    localStorage.removeItem(TOUR_STORAGE_KEY);
  }
}
